import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Provider {
  id: string;
  user_id: string;
  business_name: string;
  provider_type: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  country: string;
  verified: boolean;
  subscription_status: string;
  provider_payment_status: string;
  paypal_provider_subscription_id: string | null;
  rating: number;
  total_reviews: number;
  total_views: number;
  total_contacts: number;
  created_at: string;
  subscription_start_date: string | null;
  subscription_end_date: string | null;
  discount_applied?: number;
  discount_type?: 'percentage' | 'fixed' | 'none';
  special_plan_notes?: string;
  manually_activated?: boolean;
  manually_activated_by?: string;
  manually_activated_at?: string;
  last_paypal_sync?: string;
  paypal_sync_status?: string;
  // Verification documents - only visible to admins
  id_document_front_url?: string;
  id_document_back_url?: string;
  credential_document_url?: string;
  registration_number?: string;
}

interface ProviderFilters {
  status?: 'pending_payment' | 'active' | 'suspended' | 'cancelled' | 'expired' | 'pending_verification' | 'rejected' | 'all';
  paymentStatus?: 'pending' | 'active' | 'cancelled' | 'expired' | 'failed' | 'all';
  verified?: boolean | 'all';
  search?: string;
}

export const useAdminProviders = (filters?: ProviderFilters) => {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  // Fetch all providers with filters
  const { data: providers, isLoading: loadingProviders } = useQuery({
    queryKey: ['admin-providers', filters],
    queryFn: async () => {
      let query = supabase
        .from('service_providers')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('subscription_status', filters.status as any);
      }

      if (filters?.paymentStatus && filters.paymentStatus !== 'all') {
        query = query.eq('provider_payment_status', filters.paymentStatus as any);
      }

      if (filters?.verified !== 'all' && typeof filters?.verified === 'boolean') {
        query = query.eq('verified', filters.verified);
      }

      if (filters?.search) {
        query = query.or(`business_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Provider[];
    }
  });

  // Verify provider
  const verifyProvider = useMutation({
    mutationFn: async (providerId: string) => {
      const { data, error } = await supabase.functions.invoke('admin-manage-provider', {
        body: { action: 'verify', providerId }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-providers'] });
      toast.success('Provider verified successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to verify provider');
    }
  });

  // Reject provider
  const rejectProvider = useMutation({
    mutationFn: async (providerId: string) => {
      const { data, error } = await supabase.functions.invoke('admin-manage-provider', {
        body: { action: 'reject', providerId }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-providers'] });
      toast.success('Provider rejected');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to reject provider');
    }
  });

  // Update provider
  const updateProvider = useMutation({
    mutationFn: async ({ providerId, updates }: { providerId: string; updates: any }) => {
      const { data, error } = await supabase.functions.invoke('admin-manage-provider', {
        body: { action: 'update', providerId, updates }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-providers'] });
      toast.success('Provider updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update provider');
    }
  });

  // Delete provider (soft delete)
  const deleteProvider = useMutation({
    mutationFn: async (providerId: string) => {
      const { data, error } = await supabase.functions.invoke('admin-manage-provider', {
        body: { action: 'delete', providerId }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-providers'] });
      toast.success('Provider deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete provider');
    }
  });

  // Suspend provider
  const suspendProvider = useMutation({
    mutationFn: async (providerId: string) => {
      const { data, error } = await supabase.functions.invoke('admin-manage-provider', {
        body: { action: 'suspend', providerId }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-providers'] });
      toast.success('Provider suspended successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to suspend provider');
    }
  });

  // Reactivate provider
  const reactivateProvider = useMutation({
    mutationFn: async (providerId: string) => {
      const { data, error } = await supabase.functions.invoke('admin-manage-provider', {
        body: { action: 'reactivate', providerId }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-providers'] });
      toast.success('Provider reactivated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to reactivate provider');
    }
  });

  // Grant manual access (without payment)
  const grantProviderAccess = useMutation({
    mutationFn: async ({ providerId, reason }: { providerId: string; reason?: string }) => {
      const { data, error } = await supabase.functions.invoke('admin-manage-provider', {
        body: { action: 'grant_access', providerId, updates: { reason } }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-providers'] });
      toast.success('Provider access granted manually');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to grant provider access');
    }
  });

  // Update subscription dates
  const updateProviderDates = useMutation({
    mutationFn: async ({ 
      providerId, 
      subscription_start_date, 
      subscription_end_date 
    }: { 
      providerId: string; 
      subscription_start_date: string; 
      subscription_end_date: string 
    }) => {
      const { data, error } = await supabase.functions.invoke('admin-manage-provider', {
        body: { 
          action: 'update_dates', 
          providerId, 
          updates: { subscription_start_date, subscription_end_date } 
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-providers'] });
      toast.success('Subscription dates updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update dates');
    }
  });

  // Apply discount
  const applyDiscount = useMutation({
    mutationFn: async ({ 
      providerId, 
      discount_applied, 
      discount_type, 
      special_plan_notes 
    }: { 
      providerId: string; 
      discount_applied: number; 
      discount_type: 'percentage' | 'fixed' | 'none'; 
      special_plan_notes?: string 
    }) => {
      const { data, error } = await supabase.functions.invoke('admin-manage-provider', {
        body: { 
          action: 'apply_discount', 
          providerId, 
          updates: { discount_applied, discount_type, special_plan_notes } 
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-providers'] });
      toast.success('Discount applied successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to apply discount');
    }
  });

  // Sync PayPal status
  const syncPayPalStatus = useMutation({
    mutationFn: async (providerId: string) => {
      const { data, error } = await supabase.functions.invoke('admin-manage-provider', {
        body: { action: 'sync_paypal', providerId }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-providers'] });
      toast.success(`PayPal synced: ${data.paypalStatus}`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to sync PayPal status');
    }
  });

  // Get provider statistics
  const { data: stats } = useQuery({
    queryKey: ['admin-provider-stats'],
    queryFn: async () => {
      const { data: allProviders, error } = await supabase
        .from('service_providers')
        .select('subscription_status, provider_payment_status, verified')
        .is('deleted_at', null);

      if (error) throw error;

      const total = allProviders.length;
      const pending = allProviders.filter(p => !p.verified).length;
      const active = allProviders.filter(p => 
        p.verified && 
        (p.subscription_status as string) === 'active' && 
        p.provider_payment_status === 'active'
      ).length;
      const suspended = allProviders.filter(p => (p.subscription_status as string) === 'suspended').length;
      const paymentIssues = allProviders.filter(p => 
        p.provider_payment_status === 'cancelled' || 
        p.provider_payment_status === 'expired'
      ).length;

      return { total, pending, active, suspended, paymentIssues };
    }
  });

  return {
    providers,
    stats,
    isLoading: loadingProviders || isLoading,
    verifyProvider: verifyProvider.mutate,
    rejectProvider: rejectProvider.mutate,
    updateProvider: updateProvider.mutate,
    deleteProvider: deleteProvider.mutate,
    suspendProvider: suspendProvider.mutate,
    reactivateProvider: reactivateProvider.mutate,
    grantProviderAccess: grantProviderAccess.mutate,
    updateProviderDates: updateProviderDates.mutate,
    applyDiscount: applyDiscount.mutate,
    syncPayPalStatus: syncPayPalStatus.mutate,
    isVerifying: verifyProvider.isPending,
    isRejecting: rejectProvider.isPending,
    isUpdating: updateProvider.isPending,
    isDeleting: deleteProvider.isPending,
    isGrantingAccess: grantProviderAccess.isPending,
    isUpdatingDates: updateProviderDates.isPending,
    isApplyingDiscount: applyDiscount.isPending,
    isSyncingPayPal: syncPayPalStatus.isPending,
  };
};
